import React, { memo, Ref, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { VariableSizeGrid as Grid, GridChildComponentProps, areEqual } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { Box, BoxProps, Flex, Text } from 'rebass'
import _ from 'lodash'
import { isShowCardReviewComponent } from 'components/utils'

import {
    MAX_PAGE_SIZE,
    selectActivePageNum,
    selectSelectedCampaignUuid,
    selectSelectedSubmissionView,
    selectSidebarStep,
    selectSidebarTask,
    selectReviewPendingSubmission,
    selectSelectedCampaign,
    selectCheckCanReviewSubmissionByStep,
    selectSelectedCampaignSummary,
    selectSubmissionListParams,
    resetMultiSelectSetting,
} from 'state/review'
import {
    clearSubmissionsList,
    getSubmissionsPageList,
    SubmissionState,
    SUBMISSION_VIEW_ENUM,
    selectIsPendingSubmitSubmissionExist,
    cancelSubmissionPageList,
    SUBMISSION_TASK_STATUS_ENUM,
} from 'state/submissions'
import EmptyState from '../EmptyState'
import { selectRequestsAreLoading, selectSubmissionsList } from 'state'
import { theme } from 'theme'
import styled from 'particles/themes/styled'
import ProgressBlock from './ProgressBlock'
import ReviewTable from './ReviewTable'
import { STATE_KEYS } from 'state/requests'
import MassSubmissionCheckbox from '../MassSubmissionCheckbox'
import { requestProfileOptions } from 'state/constants'
import { STEP_TYPE_ENUM } from 'services/api/constants'
import { EmptyStateWrapper, SubmissionsWrapper } from './styles'
import { isAllowedReviewStatus, isNoneSubmitBlockExisted } from 'components/utils/submission'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { clearSubmissionFilters } from 'state/filter'
import { useIsMobileView } from 'hooks'
import { useHistory, useLocation } from 'react-router'
import NewPartiposterProfile from '../NewPartiposterProfile'

interface SubmissionCardListProps {
    submissions: SubmissionState[]
}

const SubmissionCardList: React.FC<SubmissionCardListProps> = ({ submissions }) => {
    return (
        <>
            {submissions.map(submission => (
                <SubmissionCard key={submission.uuid} submission={submission} />
            ))}
        </>
    )
}

interface SubmissionContainerProps {
    isBulkActionErr?: boolean
    hoverable?: boolean
}

export const SubmissionContainer = styled(Flex)<SubmissionContainerProps>`
    background: ${theme.newColors.neutral50};
    /* Disable - light 3 */

    border: 1px solid
        ${({ theme, isBulkActionErr }) => (isBulkActionErr ? theme.colors.border.error : theme.colors.border.primary)};
    box-sizing: border-box;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.25);

    border-radius: 4px;
    height: -webkit-fill-available;
    && {
        margin: 6px;
    }
    ${({ hoverable, theme }) =>
        hoverable &&
        `
        &:hover {
            outline: 2px solid ${theme.colors.border.actionFocus};
            outline-offset: -1px;
        }`}
    @media screen and (max-width: 768px) {
        height: auto;
    }
`

interface SubmissionCardProps {
    submission: any // TODO set to any for now as we need to refactor the whole Submission List flow to fix this
    rowRef?: Ref<HTMLDivElement>
    boxStyle?: BoxProps
}

const SubmissionCard: React.FC<SubmissionCardProps> = ({ submission, rowRef, boxStyle }) => {
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const sidebarStep = useSelector(selectSidebarStep)
    const sidebarTask = useSelector(selectSidebarTask)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const checkCanReviewSubmission = useSelector(selectCheckCanReviewSubmissionByStep)
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const isPendingView = selectedSubmissionView === SUBMISSION_VIEW_ENUM.PENDING
    const isBulkActionErr = submission && submission.error ? true : false

    const getCardCheckBox = () => {
        const ableToReview =
            isAllowedReviewStatus(selectedSubmissionView) && sidebarStep && checkCanReviewSubmission(sidebarStep.type)
        const isCampaignEnded = campaignSummary?.status === CAMPAIGN_STATUS_ENUM.CLOSED
        if (ableToReview && !isCampaignEnded) {
            return <MassSubmissionCheckbox submission={submission} />
        }
        return null
    }

    const handleVerify = () => {
        // only allow pending status (comparing both status and sidebar view because for_review has the same status as pending)
        if (
            submission?.submission_task?.status === SUBMISSION_TASK_STATUS_ENUM.PENDING &&
            selectedSubmissionView === SUBMISSION_VIEW_ENUM.PENDING
        ) {
            dispatch(resetMultiSelectSetting())

            const searchParam = new URLSearchParams(location.search)
            if (submission?.uuid) searchParam.set('reviewId', submission.uuid)
            history.push('/submissions/verify?' + searchParam.toString())
        }
    }

    if (!sidebarStep || !sidebarTask) {
        return null
    }
    return (
        <SubmissionContainer
            flexDirection="column"
            isBulkActionErr={isBulkActionErr}
            sx={{
                textAlign: 'center',
                boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
                borderRadius: '4px',
                display: 'flex',
                position: 'relative',
            }}
            hoverable={isPendingView}
            onClick={handleVerify}
        >
            <Box
                ref={rowRef}
                p={theme.spacing.xxs}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
                {...boxStyle}
            >
                <div>
                    <NewPartiposterProfile submission={submission} submissionUuid={submission.submission_uuid} />
                    {getCardCheckBox()}
                </div>

                {isBulkActionErr && (
                    <Text fontSize="0.8em" textAlign="left" color={theme.colors.frenchRose} my="6px">
                        {submission.error}
                    </Text>
                )}
                <ProgressBlock submissionProgress={submission} step={sidebarStep} task={sidebarTask} />
            </Box>
        </SubmissionContainer>
    )
}

const Cell: React.FC<GridChildComponentProps> = memo(({ columnIndex, rowIndex, style, data }) => {
    const { submissions, setRowHeight, isRowColumnMaxHeight } = data
    const index = columnIndex + rowIndex * 3
    const submission = index < submissions.length ? submissions[index] : {}
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const isPendingSubmission =
        submission?.submission_task?.status === SUBMISSION_TASK_STATUS_ENUM.PENDING &&
        selectedSubmissionView === SUBMISSION_VIEW_ENUM.PENDING

    const rowRef = useRef<any>({})

    useEffect(() => {
        if (rowRef.current) {
            setRowHeight(rowIndex, columnIndex, rowRef.current.clientHeight)
        }
    }, [rowRef, rowRef.current && rowRef.current.clientHeight])

    return (
        <Box
            style={{
                ...style,
                cursor: isPendingSubmission ? 'pointer' : 'unset',
            }}
            key={submission.uuid}
            mb={theme.spacing.xxxs}
        >
            {!_.isEmpty(submission) ? (
                <SubmissionCard
                    submission={submission}
                    rowRef={rowRef}
                    boxStyle={{
                        ...(!isRowColumnMaxHeight(rowIndex, columnIndex) && {
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        }),
                    }}
                />
            ) : (
                <></>
            )}
        </Box>
    )
}, areEqual)
Cell.displayName = 'Cell'

const COLUMN_COUNT = 3

interface SubmissionProps {
    submissions: SubmissionState[]
}

const SubmissionCards: React.FC<SubmissionProps> = ({ submissions }) => {
    const isMobileView = useIsMobileView()
    const rowHeights = useRef<any>({})
    const gridRef = useRef<any>({})
    const maxHeightRowColumns = useRef<any>({})

    const getRowHeight = (index: number): number => {
        return rowHeights.current[index] || 650
    }

    const setRowHeight = (rowIndex: number, columnIndex: number, size: number): void => {
        const currentSize = rowHeights.current[rowIndex] ? rowHeights.current[rowIndex] : 0
        if (size >= currentSize + 40) {
            gridRef.current.resetAfterRowIndex(0)
            if (maxHeightRowColumns.current[rowIndex]) {
                Object.keys(maxHeightRowColumns.current[rowIndex]).forEach(key => {
                    maxHeightRowColumns.current[rowIndex][key] = false
                })
            }
            maxHeightRowColumns.current[rowIndex] = { ...maxHeightRowColumns.current[rowIndex], [columnIndex]: true }
            rowHeights.current = { ...rowHeights.current, [rowIndex]: Math.max(currentSize, size) }
        } else {
            maxHeightRowColumns.current[rowIndex] = { ...maxHeightRowColumns.current[rowIndex], [columnIndex]: false }
        }
    }

    const isRowColumnMaxHeight = (rowIndex: number, columnIndex: number): boolean => {
        try {
            return maxHeightRowColumns.current[rowIndex][columnIndex]
        } catch {
            return true
        }
    }

    const rowCount = Math.ceil(submissions.length / COLUMN_COUNT)

    const totalHeight = useMemo(() => {
        let sum = 0
        // Loop up to the calculated rowCount
        for (let i = 0; i < rowCount; i++) {
            sum += getRowHeight(i) + 40
        }
        return sum
    }, [rowCount, getRowHeight])

    return isMobileView ? (
        <SubmissionCardList submissions={submissions} />
    ) : (
        <SubmissionsWrapper height={totalHeight}>
            <AutoSizer className="autoSizer">
                {({ height, width }) => {
                    return (
                        <Grid
                            ref={gridRef}
                            columnCount={COLUMN_COUNT}
                            rowCount={rowCount}
                            height={height}
                            width={width}
                            columnWidth={(): number => (width - 30) / COLUMN_COUNT}
                            rowHeight={index => getRowHeight(index) + 40}
                            itemData={{
                                submissions,
                                setRowHeight,
                                isRowColumnMaxHeight,
                            }}
                        >
                            {Cell}
                        </Grid>
                    )
                }}
            </AutoSizer>
        </SubmissionsWrapper>
    )
}

const SubmissionList: React.FC<any> = props => {
    const dispatch = useDispatch()

    const submissions = useSelector(selectSubmissionsList)
    const selectedCampaignUuid = useSelector(selectSelectedCampaignUuid)
    const selectedCampaign = useSelector(selectSelectedCampaign)
    const sidebarStep = useSelector(selectSidebarStep)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const submissionListParams = useSelector(selectSubmissionListParams)
    const activePageNum = useSelector(selectActivePageNum)
    const reviewPendingSubmission = useSelector(selectReviewPendingSubmission)
    const isPendingSubmissionExist = useSelector(selectIsPendingSubmitSubmissionExist)
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.SubmissionsList]))
    const offset: number = activePageNum * MAX_PAGE_SIZE

    useEffect(() => {
        if (selectedCampaign) dispatch(requestProfileOptions({ country: selectedCampaign.country }))
    }, [selectedCampaign])

    useEffect(() => {
        if (selectedCampaignUuid && sidebarStep) {
            dispatch(
                getSubmissionsPageList({
                    offset,
                    campaignUuid: selectedCampaignUuid,
                    stepUuid: sidebarStep.uuid,
                    ...submissionListParams,
                }),
            )
        }

        return () => {
            dispatch(clearSubmissionsList())
            dispatch(clearSubmissionFilters())
            // calling the cancel action to prevent the display of previous submission data on the dropout/reject page if the user swiftly switches to that page.
            dispatch(cancelSubmissionPageList())
        }
    }, [
        selectedCampaignUuid,
        sidebarStep,
        selectedSubmissionView,
        submissionListParams.tags,
        submissionListParams.keywords,
        submissionListParams.vote_type,
        submissionListParams.resubmitted,
        activePageNum,
        reviewPendingSubmission,
    ])

    if (!sidebarStep || (sidebarStep && isShowCardReviewComponent(sidebarStep.type) && isLoading)) {
        return null
    }
    if (
        (!isPendingSubmissionExist && reviewPendingSubmission != null) ||
        (sidebarStep && isShowCardReviewComponent(sidebarStep.type) && submissions.length === 0)
    ) {
        return (
            <EmptyStateWrapper>
                <EmptyState />
            </EmptyStateWrapper>
        )
    }

    switch (sidebarStep.type) {
        case STEP_TYPE_ENUM.PURCHASE_PRODUCT:
        case STEP_TYPE_ENUM.ATTEND_EVENT:
        case STEP_TYPE_ENUM.SUBMIT_PROOF_FB_URL:
        case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_FACEBOOK_POST_URL:
        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_FACEBOOK_POST:
        case STEP_TYPE_ENUM.POST_TO_IG_STORY:
        case STEP_TYPE_ENUM.SUBMIT_PROOF_IG_URL:
        case STEP_TYPE_ENUM.SUBMIT_PROOF_IG_SCREENSHOT:
        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_SCREENSHOT:
        case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_POST_URL:
        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_POST:
        case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_TIKTOK_POST_URL:
        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_TIKTOK_POST:
        case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_REEL_URL:
        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_INSTAGRAM_REEL:
        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_TWITTER_POST:
        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_YOUTUBE_VIDEO:
        case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_XIAOHONGSHU_POST_URL:
        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_XIAOHONGSHU_POST:
        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_OTHERS:
        case STEP_TYPE_ENUM.CUSTOM:
            const isNoneSubmitCustomStep =
                sidebarStep.type === STEP_TYPE_ENUM.CUSTOM && isNoneSubmitBlockExisted(sidebarStep)

            if (sidebarStep.type !== STEP_TYPE_ENUM.CUSTOM || isNoneSubmitCustomStep) {
                return <ReviewTable submissions={submissions} {...props} />
            }
    }
    return <SubmissionCards submissions={submissions} {...props} />
}

export default SubmissionList
